/**
 * DEMO MODE — a fake Twilio Voice Device.
 *
 * Drop-in stand-in for `new Device(token)` that implements the exact slice of the
 * Twilio Voice SDK this app actually uses. With it mounted, every dialer surface —
 * the call widget, manual dial, power/multi-line/predictive queues — behaves
 * normally: the device registers, calls ring, connect, run a duration timer, show a
 * live audio meter, mute, send DTMF and hang up.
 *
 * Nothing touches Twilio. No tokens are fetched, no WebRTC is opened, no numbers are
 * dialed, nothing is billed. That makes it safe to hand a prospect a live sandbox.
 *
 * Enable with NEXT_PUBLIC_DEMO_MODE=true (build-time, so it can never flip on in prod
 * by accident).
 *
 * Surface covered — taken from what the codebase calls, not from the whole SDK:
 *   Device : on / connect / register / updateToken
 *            events: registered, unregistered, incoming, error, tokenWillExpire
 *   Call   : on / off / accept / reject / disconnect / mute / isMuted / status
 *            sendDigits / getLocalStream
 *            events: accept, disconnect, cancel, reject, error, mute, volume
 */

// All-in-one (2026-07-28): the demo verdict is RUNTIME (hostname) rather than a
// build-time env snapshot. Re-exported here because ~24 modules import it from this
// path; the util owns the logic.
export { isDemoMode } from '../utils/demoMode';
import { isDemoMode as __isDemoMode } from '../utils/demoMode';

/**
 * Caller ID for the unprompted inbound demo call. Point this at a phone number that
 * exists in the seeded demo leads and the screen pop shows a real lead card instead of
 * an unknown caller — which is the whole point of showing inbound at all.
 */
const DEMO_INBOUND_FROM = process.env.NEXT_PUBLIC_DEMO_INBOUND_FROM || '+16145550188';

/**
 * Unprompted inbound is OFF unless a delay is explicitly configured.
 *
 * It rang itself 75s after load to show the screen-pop without anyone having to find a
 * button. In practice it interrupts whatever the rep is demoing, and it announces
 * "Unknown Caller" whenever the configured number doesn't match a seeded lead — which
 * reads as a bug, not a feature. Opt in with NEXT_PUBLIC_DEMO_INBOUND_DELAY_MS, and set
 * NEXT_PUBLIC_DEMO_INBOUND_FROM to a real seeded lead number when you do.
 *
 * `window.__ic_demoDevice.simulateIncoming()` still triggers one on demand for a
 * scripted walkthrough.
 */
const DEMO_INBOUND_DELAY_MS = Number(process.env.NEXT_PUBLIC_DEMO_INBOUND_DELAY_MS) || 0;

type Handler = (...args: any[]) => void;

/** Minimal event emitter matching the SDK's on/off semantics. */
class Emitter {
  private map = new Map<string, Set<Handler>>();

  on(evt: string, fn: Handler) {
    if (!this.map.has(evt)) this.map.set(evt, new Set());
    this.map.get(evt)!.add(fn);
    return this;
  }

  off(evt: string, fn?: Handler) {
    if (!fn) this.map.delete(evt);
    else this.map.get(evt)?.delete(fn);
    return this;
  }

  /** The real SDK has this and the context calls it on cleanup. */
  removeAllListeners(evt?: string) {
    if (evt) this.map.delete(evt);
    else this.map.clear();
    return this;
  }

  protected emit(evt: string, ...args: any[]) {
    this.map.get(evt)?.forEach((fn) => {
      try {
        fn(...args);
      } catch {
        /* a listener throwing must not kill the simulation */
      }
    });
  }
}

export type DemoCallOutcome = 'answered' | 'no-answer' | 'busy';

class DemoCall extends Emitter {
  parameters: Record<string, string>;
  customParameters = new Map<string, string>();

  private _status: 'pending' | 'ringing' | 'open' | 'closed' = 'pending';
  private _muted = false;
  private _timers: any[] = [];
  private _volumeTimer: any = null;

  constructor(
    params: Record<string, string>,
    outcome: DemoCallOutcome,
    ringMsOverride?: number,
    isConferenceJoin = false,
  ) {
    super();
    this.parameters = { CallSid: `CAdemo${Math.random().toString(36).slice(2, 12)}`, ...params };
    Object.entries(params || {}).forEach(([k, v]) => this.customParameters.set(k, String(v)));

    // Ring for a beat, then resolve to the outcome — mirrors real call timing so the
    // UI's ringing → connected transitions are exercised, not skipped.
    this._status = 'ringing';
    const ringMs = ringMsOverride ?? 1600 + Math.floor(Math.random() * 1800);

    this._timers.push(
      setTimeout(() => {
        if (this._status === 'closed') return;

        if (outcome === 'answered') {
          this._status = 'open';
          this.emit('accept', this);
          this.startVolume();

          // ⚠️ `accept` alone leaves the widget stuck on "Ringing…".
          //
          // On an OUTBOUND call the context deliberately sets "ringing" when the agent's
          // own leg connects, because that only means the agent is on the line — the
          // customer hasn't picked up yet. It moves to "in-progress" solely when a
          // `customer_answered` broadcast arrives from the backend.
          //
          // A manual or single-lead dial in the sandbox never reaches the backend: this
          // fake Device resolves the whole call in the browser, so that broadcast is
          // never sent and the call rings forever. Multi-line escapes this only because
          // its answer verdict genuinely does come from the server.
          //
          // So emit the same signal locally, over the window-event channel the app
          // already uses to relay socket payloads across contexts (see transfer:offer).
          // Skipped for a conference join, where the real broadcast still arrives and
          // this would race it.
          // Dispatched for EVERY answered call, including conference joins.
          //
          // This was originally skipped for conference joins on the assumption that only
          // multi-line uses them and its real broadcast would arrive anyway. Wrong twice:
          // the manual dialer also routes through a conference, so the guard silently
          // swallowed the one case it was meant to fix — the call went straight to 'open'
          // (conference joins always answer) and nothing ever told the UI. That is why
          // the badge sat on "Ringing…" while the device held an open call.
          //
          // A duplicate for multi-line is harmless: both this and the real broadcast set
          // the same 'in-progress', and handleCustomerAnswered is idempotent.
          {
            this._timers.push(
              setTimeout(() => {
                if (this._status !== 'open') return;
                try {
                  window.dispatchEvent(
                    new CustomEvent('insuracentral-demo-customer-answered', {
                      detail: {
                        parentCallSid: this.parameters.CallSid,
                        customerCallSid: `CAdemocust${Math.random().toString(36).slice(2, 10)}`,
                        phoneNumber: String(params.To || ''),
                        customerPhone: String(params.To || ''),
                      },
                    }),
                  );
                } catch {
                  /* non-browser context — nothing to notify */
                }
                // Short, deliberate beat: the agent hears one ring before the lead
                // speaks, which is what makes the transition read as a real pickup
                // rather than an instant jump.
              }, 700),
            );
          }
        } else {
          this._status = 'closed';
          this.stopVolume();
          // 'cancel' is what the app treats as an unanswered outbound call
          this.emit(outcome === 'busy' ? 'error' : 'cancel', outcome === 'busy'
            ? { code: 31486, message: 'Busy (demo)' }
            : undefined);
          this.emit('disconnect', this);
        }
      }, ringMs),
    );
  }

  /** Drives the audio meter so the widget looks alive while "talking". */
  private startVolume() {
    this._volumeTimer = setInterval(() => {
      if (this._status !== 'open') return;
      // speech-like envelope rather than flat noise
      const base = 0.12 + Math.random() * 0.5;
      const spike = Math.random() < 0.18 ? Math.random() * 0.3 : 0;
      this.emit('volume', Math.min(1, base + spike), Math.min(1, base * 0.7));
    }, 120);
  }

  private stopVolume() {
    if (this._volumeTimer) clearInterval(this._volumeTimer);
    this._volumeTimer = null;
  }

  status() {
    return this._status;
  }

  accept() {
    if (this._status === 'closed') return;
    this._status = 'open';
    this.emit('accept', this);
    this.startVolume();
  }

  reject() {
    this._status = 'closed';
    this.stopVolume();
    this.emit('reject');
    this.emit('disconnect', this);
  }

  disconnect() {
    if (this._status === 'closed') return;
    this._status = 'closed';
    this.stopVolume();
    this._timers.forEach(clearTimeout);
    this.emit('disconnect', this);
  }

  mute(shouldMute = true) {
    this._muted = shouldMute;
    this.emit('mute', shouldMute, this);
  }

  isMuted() {
    return this._muted;
  }

  sendDigits(digits: string) {
    // No-op: nothing to send. Kept so DTMF UI (key grabber, IVR nav) still works.
    return digits;
  }

  /** Real code calls this for audio visualisation; a silent track keeps it happy. */
  getLocalStream(): MediaStream | null {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const dst = ctx.createMediaStreamDestination();
      return dst.stream;
    } catch {
      return null;
    }
  }
}

class DemoDevice extends Emitter {
  audio = {
    // The app reads/sets these; make them harmless no-ops.
    availableInputDevices: new Map(),
    availableOutputDevices: new Map(),
    setInputDevice: async () => {},
    speakerDevices: { set: async () => {}, get: () => new Set() },
    ringtoneDevices: { set: async () => {}, get: () => new Set() },
    disconnect: () => {},
    incoming: () => {},
    outgoing: () => {},
  };

  private _calls: DemoCall[] = [];

  /**
   * Mirrors Device.state. The context reconnects whenever this isn't 'registered' —
   * on tab focus, on visibility change — so a missing property left the widget stuck
   * showing "Network connection lost", and the reconnect path then tried to fetch a
   * real Twilio token. The demo device is always registered; there's nothing to drop.
   */
  state: 'registered' | 'unregistered' | 'destroyed' = 'registered';

  /** The SDK exposes this; the widget reads it to decide if a call is up. */
  get isBusy(): boolean {
    return this._calls.some((c) => c.status() === 'open' || c.status() === 'ringing');
  }

  constructor() {
    super();
    // Register on the next tick so listeners attached after construction still fire —
    // the real SDK is async here too.
    setTimeout(() => this.emit('registered'), 350);
    this.scheduleDemoInbound();
  }

  async connect(opts: { params?: Record<string, string> } = {}) {
    const params = opts.params || {};
    const to = String(params.To || '');

    // Joining a conference is NOT a dial — the lead has already picked up and the
    // server put them in a room. This leg is the agent's own audio, so it must always
    // connect. Rolling the outcome here was why multi-line dialed fine but never
    // showed the active-call UI: the agent's join could "not answer".
    const isConferenceJoin = to.startsWith('multiline:') || to.startsWith('conference:');

    // Every dial placed through THIS device connects.
    //
    // It used to roll 72/21/7 answered/no-answer/busy for a lifelike mix. That belongs in
    // bulk dialing — and it still happens there, because power and multi-line outcomes
    // come from the backend simulator (DemoTwilioClient.runCall), not from this device.
    // What actually reaches here is the agent's own leg: a manual dial, a single-lead
    // dial, or a conference join.
    //
    // For those, a random miss is just a broken demo. Someone deliberately types a number,
    // presses call, and 28% of the time nothing happens — indistinguishable from a bug,
    // and it masked a real one while we were chasing it. A conference join was already
    // forced to answer for the same reason: that leg is the agent joining a room the lead
    // is already in, so "not answering" is nonsense.
    const outcome: DemoCallOutcome = 'answered';

    // A conference join is near-instant; an outbound call rings first.
    const call = new DemoCall(params, outcome, isConferenceJoin ? 250 : undefined, isConferenceJoin);
    this._calls.push(call);
    return call as any;
  }

  async register() {
    this.state = 'registered';
    this.emit('registered');
  }

  async unregister() {
    this.state = 'unregistered';
    this.emit('unregistered');
  }

  async updateToken(_t: string) {
    /* nothing to refresh */
  }

  disconnectAll() {
    this._calls.forEach((c) => c.disconnect());
    this._calls = [];
  }

  destroy() {
    this.disconnectAll();
    this.state = 'destroyed';
  }

  /** Lets a demo script trigger an inbound call to show the incoming-call UI. */
  simulateIncoming(params: Record<string, string> = {}) {
    const call = new DemoCall({ From: DEMO_INBOUND_FROM, ...params }, 'answered');
    (call as any)._status = 'pending';
    this.emit('incoming', call);
    return call;
  }

  /**
   * Ring once, unprompted, a little after the device comes up.
   *
   * Inbound is half the product — the screen pop, the accept/reject UI, the call
   * landing on the lead — and nobody discovers it by clicking around, because there's
   * no button that says "receive a call". So the sandbox places one itself.
   *
   * Once per session, and never while a call is already up, so it can't interrupt
   * whatever the prospect is in the middle of.
   */
  private scheduleDemoInbound() {
    if (!DEMO_INBOUND_DELAY_MS) return; // opt-in only — see the constant above

    setTimeout(() => {
      const busy = this._calls.some((c) => c.status() !== 'closed');
      if (busy) return;
      this.simulateIncoming();
    }, DEMO_INBOUND_DELAY_MS);
  }
}

/** Factory used in place of `new Device(token, opts)` when demo mode is on. */
export function createDemoDevice(): any {
  const d = new DemoDevice();
  if (typeof window !== 'undefined') {
    // Handy for demo scripting / QA: window.__ic_demoDevice.simulateIncoming()
    (window as any).__ic_demoDevice = d;
  }
  return d;
}
